// Tiny fetch wrapper for the admin API.
//
// Same-origin requests (`/api/admin/...`) — the session cookie is sent
// automatically. Errors are normalised into ApiClientError so callers can
// branch on `code` instead of HTTP status alone.

import type { ApiError } from "./types";

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.name = "ApiClientError";
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | undefined>;
}

const BASE = "/api/admin";

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined && v !== "") url.searchParams.set(k, String(v));
    }
  }

  const init: RequestInit = {
    method: opts.method ?? "GET",
    credentials: "same-origin",
  };

  if (opts.body !== undefined) {
    init.headers = { "content-type": "application/json" };
    init.body = JSON.stringify(opts.body);
  }

  const res = await fetch(url.toString(), init);

  if (res.status === 204) {
    return undefined as T;
  }

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new ApiClientError(res.status, "invalid_response", text.slice(0, 200));
    }
  }

  if (!res.ok) {
    const err = (parsed as ApiError)?.error;
    throw new ApiClientError(
      res.status,
      err?.code || `http_${res.status}`,
      err?.message || res.statusText,
    );
  }

  return parsed as T;
}
